import { DrilldownFilterContext } from 'features/drilldown/data/DrilldownFilterContext'
import { useGeckoNotifications } from 'features/notifications'
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { GenericPanelData } from 'types'

import { isValidPanelData, isValidSetPanelData } from './validation'

export interface DrilldownPanelDataContextType<T extends GenericPanelData> {
  panelData: T
  setPanelData: React.Dispatch<React.SetStateAction<T>>
  defaultPanelData: T
}

export const DrilldownPanelDataContext =
  createContext<DrilldownPanelDataContextType<GenericPanelData> | null>(null)

/**
 * This hook returns a panel's data and the function to set that panel data.
 * It is designed with the idea that one context wraps each panel.
 * @returns The panel data and the function to set the panel data.
 */
export function useDrilldownPanelData<
  T extends GenericPanelData,
>(): DrilldownPanelDataContextType<T> {
  const context = useContext(DrilldownPanelDataContext)
  if (context === null) {
    throw new Error(
      'useDrilldownPanelData must be used within a DrilldownPanelDataProvider'
    )
  }
  if (!isValidPanelData(context.panelData)) {
    throw new Error(
      'DrilldownPanelDataProvider must be initialised with a valid panelData'
    )
  }

  if (!isValidSetPanelData(context.setPanelData)) {
    throw new Error(
      'DrilldownPanelDataProvider must be initialised with a valid setPanelData function'
    )
  }

  if (!isValidPanelData(context.defaultPanelData)) {
    throw new Error(
      'DrilldownPanelDataProvider must be initialised with a valid defaultPanelData'
    )
  }

  return useMemo(
    () => ({
      panelData: context.panelData as T,
      setPanelData: context.setPanelData as React.Dispatch<
        React.SetStateAction<T>
      >,
      defaultPanelData: context.defaultPanelData as T,
    }),
    [context.panelData, context.setPanelData, context.defaultPanelData]
  )
}

/**
 * This hook wraps the context that gets the panel data and sets the panel data.
 * It checks if the schema version of the panel data in the context is the latest version, particularly for the case where you load a saved view which was
 * saved with an older schema.
 *
 * If it is not, it sets the panel data to the default panel data.
 *
 * @returns The panel data and the function to set the panel data.
 */
export function useVersionAwareDrilldownPanelData<
  T extends GenericPanelData,
>(): DrilldownPanelDataContextType<T> {
  const { panelData, setPanelData, defaultPanelData } =
    useDrilldownPanelData<T>()
  const notificationContext = useGeckoNotifications()
  const filterContext = useContext(DrilldownFilterContext)

  useEffect(() => {
    if (
      panelData.panelDataSchemaVersion !==
      defaultPanelData.panelDataSchemaVersion
    ) {
      setPanelData(defaultPanelData)

      // Long-term/ideally we would have one definitive state and derive things like filters from that.
      // For now, we store filters separately to panel data, so when resetting a panel, clear the filters too:
      filterContext.setFilters(new Map())

      notificationContext.warningNotification(
        `A panel was saved with an older version of drilldown, the panel will be reset to its default value.`
      )
    }
  }, [
    defaultPanelData,
    filterContext,
    notificationContext,
    panelData.panelDataSchemaVersion,
    setPanelData,
  ])

  return { panelData, setPanelData, defaultPanelData }
}
